import { builtInChassisCodeList } from "../chassis-codes";
import type { MileageUnit } from "../enums";
import { bucketIndex } from "./measures";

/**
 * Who the customers are, by transparent rules (ADR 0016): what kind of vehicles they keep, how
 * old and how far driven, and which segment a garage falls in. The boundaries are first guesses;
 * a change applies from then on, and past reports keep the values they were computed with.
 */

export const VEHICLE_KINDS = ["car", "motorcycle", "unknown"] as const;
export type VehicleKind = (typeof VEHICLE_KINDS)[number];

/**
 * A vehicle's kind, known only through a built-in chassis code: BMW Motorrad's are motorcycles,
 * the others cars. Without one, or with a garage's own, it is unknown.
 */
export function vehicleKind(chassisKey: string | null | undefined): VehicleKind {
  const list = chassisKey ? builtInChassisCodeList(chassisKey) : null;
  if (list === null) return "unknown";
  return list === "motorrad" ? "motorcycle" : "car";
}

/**
 * Lower edges in years of the vehicle age buckets: 0–4, 5–9, 10–19, youngtimers (20–29) and
 * classics from 30, the age for an H plate, then 40 and over.
 */
export const VEHICLE_AGE_EDGES = [0, 5, 10, 20, 30, 40] as const;
export const YOUNGTIMER_YEARS = 20;
export const CLASSIC_YEARS = 30;

/**
 * The age bucket of a vehicle by its model year, on today's UTC date "YYYY-MM-DD"; null without
 * a model year or with one more than a year ahead.
 */
export function vehicleAgeBucket(
  modelYear: number | null | undefined,
  today: string,
): number | null {
  if (!modelYear) return null;
  const age = Number(today.slice(0, 4)) - modelYear;
  if (age < -1) return null;
  return bucketIndex(Math.max(0, age), VEHICLE_AGE_EDGES);
}

/** Kilometres in a mile. */
const KM_PER_MILE = 1.609344;

/** A mileage in km, whatever the unit it was entered in. */
export function mileageKm(mileage: number, unit: MileageUnit): number {
  return unit === "mi" ? Math.round(mileage * KM_PER_MILE) : mileage;
}

/** Lower edges in km of the mileage buckets. */
export const MILEAGE_EDGES_KM = [0, 50_000, 100_000, 150_000, 200_000, 300_000, 400_000] as const;

/** The mileage bucket of a mileage in km (`mileageKm`), or null without one. */
export function mileageBucket(km: number | null | undefined): number | null {
  if (km === null || km === undefined || km < 0) return null;
  return bucketIndex(km, MILEAGE_EDGES_KM);
}

export const GARAGE_SEGMENTS = ["no_vehicle", "one", "few", "collection", "workshop"] as const;
export type GarageSegment = (typeof GARAGE_SEGMENTS)[number];

/** The most vehicles of "a few" and of "a collection"; above that, a garage is workshop-like. */
export const FEW_VEHICLES = 5;
export const COLLECTION_VEHICLES = 15;

/**
 * A garage's segment: one vehicle, a few (2–5), a collection (6–15), or workshop-like: over 15
 * vehicles, or several members working with prices without tax (the business price basis).
 */
export function garageSegment({
  vehicles,
  members,
  pricesIncludeTax,
}: {
  vehicles: number;
  members: number;
  pricesIncludeTax: boolean;
}): GarageSegment {
  if (vehicles > COLLECTION_VEHICLES || (members > 1 && !pricesIncludeTax)) return "workshop";
  if (vehicles === 0) return "no_vehicle";
  if (vehicles === 1) return "one";
  return vehicles <= FEW_VEHICLES ? "few" : "collection";
}
